import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {matchRoutes, useLocation} from 'react-router-dom';
import {useAuthRenew} from 'services/auth/auth.hooks';
import constants from 'helpers/constants';
import utils from 'helpers/utils';
import system from 'helpers/system';
import {useRouter} from 'components/organisms/Providers/RouteProvider/RouteProvider';
import {useSystem} from 'components/organisms/Providers/SystemProvider/SystemProvider';
import {useEffectEvent, usePageVisibility, useUpdatedRef} from 'helpers/hooks/utils';
import {linkPath} from 'helpers/hooks/links';
import {useClientTeamList} from 'services/client/team/team.hooks';
import {useClientUserGet, useClientUserList} from 'services/client/user/user.hooks';
import {useClientGet, useClientWarmup} from 'services/client/client.hooks';
import {usePortalClientGet, usePortalClientWarmup} from 'services/portal/client/client.hooks';
import {useAppSetState, useAppStateValue} from 'stores/hooks/app';
import {useServiceDataByIdEx} from 'stores/hooks/store';

export const AuthContext = React.createContext(null);

export function useAuthControl () {
  return React.useContext(AuthContext);
}

export function useAuth (type = constants.appState.type.session) {
  const context = React.useContext(AuthContext);

  return (type === constants.appState.type.local) ? context?.authLocal : context?.auth;
}

export function useAuthSet (type = constants.appState.type.session) {
  const context = React.useContext(AuthContext);

  return (type === constants.appState.type.local) ? context?.authLocalSet : context?.authSet;
}

export function useAuthToken () {
  return React.useContext(AuthContext)?.token;
}

export function useAuthTokenTime () {
  return React.useContext(AuthContext)?.tokenTime;
}

export function useAuthHolderToken () {
  return React.useContext(AuthContext)?.holderToken;
}

export function useAuthHolderTokenTime () {
  return React.useContext(AuthContext)?.holderTokenTime;
}

export function useAuthWhiteLabel () {
  return React.useContext(AuthContext)?.whiteLabel;
}

export function useAuthUserId () {
  return React.useContext(AuthContext)?.userId;
}

export function useAuthClientId () {
  return React.useContext(AuthContext)?.clientId;
}

export function useAuthParentClientId () {
  return React.useContext(AuthContext)?.parentClientId;
}

export function useAuthTeamId () {
  return React.useContext(AuthContext)?.teamId;
}

export function useAuthSetTeamId () {
  return React.useContext(AuthContext)?.setTeamId;
}

export function useAuthIsVentureIq () {
  return React.useContext(AuthContext)?.ventureIq;
}

export function useAuthIsProxyClient () {
  return React.useContext(AuthContext)?.proxyClient;
}

export function useAuthIsRegular () {
  return React.useContext(AuthContext)?.regular;
}

export function useAuthIsProxy () {
  return React.useContext(AuthContext)?.proxy;
}

export function useAuthIsNormal () {
  return React.useContext(AuthContext)?.normal;
}

export function useAuthIsPortal () {
  return React.useContext(AuthContext)?.portal;
}

// not a context read: it takes a lookup descriptor and resolves it through the service store
export function useAuthLookup (lookup) {
  const lookupData0 = useServiceDataByIdEx(
    lookup?.[0]?.path || 'auth',
    lookup?.[0]?.id,
    lookup?.[0]?.params,
    lookup?.[0]?.dataType
  );
  const lookupData1 = useServiceDataByIdEx(
    lookup?.[1]?.path || 'auth',
    lookup?.[1]?.id,
    lookup?.[1]?.params,
    lookup?.[1]?.dataType
  );
  const lookupData2 = useServiceDataByIdEx(
    lookup?.[2]?.path || 'auth',
    lookup?.[2]?.id,
    lookup?.[2]?.params,
    lookup?.[2]?.dataType
  );

  return useMemo(() => {
    let lookupData = null;
    if (lookup?.[0] && lookupData0?.data) {
      lookupData = {...lookupData, [lookup?.[0].name]: lookupData0?.data};
    }
    if (lookup?.[1] && lookupData1?.data) {
      lookupData = {...lookupData, [lookup?.[1].name]: lookupData1?.data};
    }
    if (lookup?.[2] && lookupData2?.data) {
      lookupData = {...lookupData, [lookup?.[2].name]: lookupData2?.data};
    }

    return lookupData;
  }, [lookup, lookupData0?.data, lookupData1?.data, lookupData2?.data]);
}

// not a context read: it takes a callback and fires it on a transition
export function useAuthReset (callback) {
  // atom-backed: this is called from useServiceAuthorize, which runs above AuthProvider
  const {auth, clientId, teamId} = useAuthState();

  const getState = useCallback(() => ({
    loggedIn: auth?.loggedIn,
    clientId: clientId,
    teamId: teamId
  }), [auth?.loggedIn, clientId, teamId]);

  const state = useRef(getState());

  useEffect(() => {
    const oldState = state.current;
    const newState = getState();

    if (!utils.compare(oldState, newState)) {
      let type;
      if (oldState.loggedIn) {
        if (!newState.loggedIn) {
          type = constants.resetTypes.logout;
        } else if (newState.clientId !== oldState.clientId) {
          type = constants.resetTypes.client;
        } else if (newState.teamId !== oldState.teamId) {
          type = constants.resetTypes.team;
        }
      }

      if (type) {
        callback(type);
      }

      state.current = newState;
    }
  }, [getState, callback]);
}

export function useAuthClient () {
  return React.useContext(AuthContext)?.client;
}

export function useAuthUser () {
  return React.useContext(AuthContext)?.user;
}

export function useAuthGroup () {
  return React.useContext(AuthContext)?.authGroup;
}

export function useAuthTeam () {
  return React.useContext(AuthContext)?.team;
}

export function useAuthUsers () {
  return React.useContext(AuthContext)?.users;
}

export function useAuthTeams () {
  return React.useContext(AuthContext)?.teams;
}

export function useAuthPasses () {
  return React.useContext(AuthContext)?.passes;
}

export function useAuthorize () {
  const control = useAuthControl();

  return control.authorize;
}

export function useAuthorizeGroup () {
  const control = useAuthControl();

  return control.authorizeGroup;
}

export function useAuthorizeAttribute () {
  const control = useAuthControl();

  return control.authorizeAttribute;
}

export function useAuthorizeAction () {
  const control = useAuthControl();

  return control.authorizeAction;
}

export function useAuthUserStats () {
  return React.useContext(AuthContext)?.userStats;
}

// Reads the atoms directly and derives everything from them. AuthProvider uses it to build the
// context; anything rendering ABOVE AuthProvider (ServiceProvider) has to use it too, because
// there is no context to read up there. Everything else should use the context hooks above.
export function useAuthState () {
  const auth = useAppStateValue(constants.appState.type.session, 'auth', constants.appState.scope.global);
  const authSet = useAppSetState(constants.appState.type.session, 'auth', constants.appState.scope.global);
  const authLocal = useAppStateValue(constants.appState.type.local, 'auth', constants.appState.scope.global);
  const authLocalSet = useAppSetState(constants.appState.type.local, 'auth', constants.appState.scope.global);

  const token = auth?.tokens?.token ?? authLocal?.tokens?.token;
  const holderToken = auth?.tokens?.holder ?? authLocal?.tokens?.holder;
  const whiteLabel = auth?.whiteLabel ?? authLocal?.whiteLabel;

  const userId = auth?.userId;
  const clientId = auth?.clientId;
  const regular = auth?.type === constants.user.types.regular;
  const proxy = auth?.type === constants.user.types.proxy;
  const parentClientId = proxy ? auth?.client?.parentId : null;
  const ventureIq = clientId === 0;
  const proxyClient = parentClientId >= 0 && !ventureIq;

  const tokenType = utils.extractToken(token)?.tokenType?.toLowerCase();
  const normal = Boolean(tokenType === 'user');
  const portal = Boolean(tokenType === 'portal');

  const sessionTeamId = auth?.teamId ?? authLocal?.teamId;
  const teamId = utils.isDefined(sessionTeamId) ? +sessionTeamId : (clientId === 0 ? 0 : null);

  const tokenTime = useMemo(() => {
    const jwt = utils.extractToken(token);
    return jwt?.iat ? (new Date(jwt.iat * 1000)) : null;
  }, [token]);

  const holderTokenTime = useMemo(() => {
    const jwt = utils.extractToken(holderToken);
    return jwt?.iat ? (new Date(jwt.iat * 1000)) : null;
  }, [holderToken]);

  const setTeamId = useCallback((teamId) => {
    authSet((current) => ({...current, teamId}));
    authLocalSet((current) => ({...current, teamId}));
  }, [authSet, authLocalSet]);

  return {
    auth, authLocal, authSet, authLocalSet,
    token, tokenTime, holderToken, holderTokenTime, whiteLabel,
    userId, clientId, parentClientId, teamId, setTeamId,
    ventureIq, proxyClient, regular, proxy, normal, portal
  };
}

const AuthProvider = (props) => {
  const {
    auth, authLocal, authSet, authLocalSet,
    token, tokenTime, holderToken, holderTokenTime, whiteLabel,
    userId, clientId, parentClientId, teamId, setTeamId,
    ventureIq, proxyClient, regular, proxy, normal, portal
  } = useAuthState();

  const clientWarmup = useClientWarmup();
  const portalClientWarmup = usePortalClientWarmup();

  const clientGet = useClientGet({clientId}, {enabled: (clientId >= 0) && normal});
  const clientGetPortal = usePortalClientGet({clientId}, {enabled: (clientId >= 0) && portal});
  const client = useMemo(() => {
    return clientGet?.data ?? clientGetPortal?.data ?? auth?.client;
  }, [clientGet?.data, clientGetPortal?.data, auth?.client]);

  const userGet = useClientUserGet({clientId, userId}, {enabled: Boolean(normal && userId > 0)});
  const user = useMemo(() => {
    return userGet?.data ?? auth?.user;
  }, [userGet?.data, auth?.user]);

  const authGroup = useMemo(() => {
    const jwt = utils.extractToken(token);
    return userGet?.data?.authGroup ?? jwt?.authGroup
  }, [userGet?.data, token]);

  const holderAuthGroup = useMemo(() => {
    const jwt = utils.extractToken(holderToken);
    return jwt?.authGroup
  }, [holderToken]);
  
  const passes = useMemo(() => {
    return (user?.passes ?? auth?.passes ?? []).map((pass) => {
      return {
        client: utils.clone(utils.camelcase(pass.client), true),
        user: utils.clone(utils.camelcase(pass.profile), true),
      }
    }).sort((p1, p2) => {
      return p1.client.name.localeCompare(p2.client.name);
    })
  }, [user?.passes, auth?.passes]);

  const usersList = useClientUserList({clientId}, {enabled: normal && (clientId >= 0)});
  const users = useMemo(() => {
    return usersList?.data ?? [];
  }, [usersList?.data]);

  const teamsList = useClientTeamList({clientId}, {enabled: normal && (clientId >= 0)})
  const teams = useMemo(() => {
    return teamsList?.data ?? auth?.teams ?? [];
  }, [teamsList?.data, auth?.teams]);
  
  const team = useMemo(() => {
    return teams?.find((team) => +team.clientId === +teamId);
  }, [teams, teamId]);
  
  const router = useRouter();
  const location = useLocation();

  const [suspended, setSuspended] = useState(false);

  const doRenew = useAuthRenew();
  const doHolderRenew = useAuthRenew({overrideApiToken: holderToken});

  const systemInfo = useSystem();

  const atLeastLevel = useCallback((userGroup, group) => {
    let requiredLevel = constants.auth.level[utils.camelcase(group)];
    let actualLevel = constants.auth.level[utils.camelcase(userGroup || constants.auth.level.all)];

    if (utils.isNumber(requiredLevel) && utils.isNumber(actualLevel)) {
      return actualLevel >= requiredLevel;
    } else {
      return false;
    }
  }, []);

  const atMostLevel = useCallback((userGroup, group) => {
    let requiredLevel = constants.auth.level[utils.camelcase(group)];
    let actualLevel = constants.auth.level[utils.camelcase(userGroup || constants.auth.level.all)];

    if (utils.isNumber(requiredLevel) && utils.isNumber(actualLevel)) {
      return actualLevel <= requiredLevel;
    } else {
      return false;
    }
  }, []);

  const userStats = useMemo(() => {
    const stats = {};

    (users ?? []).forEach((u) => {
      Object.keys(constants.auth.group).forEach((k) => {
        const authGroup = constants.auth.group[k];

        Object.keys(constants.user.types).forEach((t) => {
          const type = constants.user.types[t];

          stats[type] = stats[type] ?? {};
          stats[type].atLeastLevel = stats[type].atLeastLevel ?? {};
          stats[type][authGroup] = stats[type][authGroup] ?? 0;
          stats[type].atLeastLevel[authGroup] = stats[type].atLeastLevel[authGroup] ?? 0;
        });

        if (u.authGroup === authGroup) {
          stats[u.type][authGroup] += 1;
        }
        if (atLeastLevel(u.authGroup, authGroup)) {
          stats[u.type].atLeastLevel[authGroup] += 1;
        }
      });
    });

    return stats;
  }, [users, atLeastLevel]);

  const authorizeGroup = useCallback((group) => {
    return atLeastLevel(authGroup, group);
  }, [authGroup, atLeastLevel]);

  const authorizeAttribute = useCallback((attribute, meta) => {
    const hasRight = (attribute, meta) => {
      const path = (attribute || '').split('.');

      let metaClient, metaCollection, metaView, metaTeam, metaUser,
        metaEntity, metaTask, metaComment, metaFile, metaPerson, metaField,
        metaSource, metaService, metaPlan, metaCollaborator;
      let field, filter, column, section, value, assistant, action, saver;

      switch (path[0]) {
        case 'client': {
          metaClient = meta?.client;
          metaPlan = meta?.plan;
          metaService = meta?.service;

          if (utils.isArray(metaClient)) {
            return !metaClient.some((client) => !hasRight(attribute, {...meta, client}));
          }

          switch (path[1]) {
            case 'route':
            case 'read':
              return !metaClient ||
                (metaClient.clientId === clientId && atLeastLevel(authGroup, constants.auth.group.commentOnly)) ||
                (metaClient.clientId !== clientId && ventureIq && atLeastLevel(authGroup, constants.auth.group.user));
            case 'switch':
              return !metaClient ? (
                regular ? (passes.filter((p) => p.client.props.isActive).length > 0) :
                (proxy && (+(metaClient ?? meta)?.clientId === clientId || +(metaClient ?? meta)?.clientId === 0))
              ) : (
                metaClient.props.isActive
              );
            case 'update':
              return (metaClient?.clientId === clientId && atLeastLevel(authGroup, constants.auth.group.clientAdmin)) ||
                (metaClient?.clientId !== clientId && ventureIq && atLeastLevel(authGroup, constants.auth.group.admin));
            case 'create':
            case 'delete':
              return ventureIq && atLeastLevel(authGroup, constants.auth.group.admin);
            case 'download':
              switch (path[2]) {
                case 'usage':
                  return ventureIq && atLeastLevel(authGroup, constants.auth.group.admin);
                default:
                  return false;
              }
            case 'plan':
              switch (path[2]) {
                case 'read':
                  return atLeastLevel(authGroup, constants.auth.group.clientAdmin);
                case 'upgrade':
                case 'downgrade':
                  return atLeastLevel(authGroup, constants.auth.group.clientAdmin) && (
                    !metaPlan || (
                      userStats?.regular?.atLeastLevel?.[constants.auth.group.client] <= metaPlan.seats &&
                      teams?.filter((t) => t.props.isActive)?.length <= metaPlan.teams
                    )
                  );
                case 'create':
                case 'update':
                case 'delete':
                default:
                  return !metaPlan ||
                    atLeastLevel(holderAuthGroup, constants.auth.group.admin);
              }
            case 'service':
              switch (path[2]) {
                case 'read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly);
                case 'create':
                case 'update':
                case 'delete':
                default:
                  return !metaService ||
                    !constants.data.lookup('services', metaService.serviceId).readOnly;
              }
            case 'statusGroup':
              switch (path[2]) {
                case 'read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly);
                case 'create':
                case 'update':
                case 'delete':
                default:
                  return atLeastLevel(authGroup, constants.auth.group.clientAdmin);
              }
            case 'connection':
              switch (path[2]) {
                case 'read':
                case 'create':
                case 'update':
                case 'delete':
                default:
                  return atLeastLevel(authGroup, constants.auth.group.clientAdmin);
              }
            case 'tagGroup':
              switch (path[2]) {
                case 'read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly);
                case 'create':
                case 'update':
                case 'delete':
                default:
                  return atLeastLevel(authGroup, constants.auth.group.clientAdmin);
              }
            case 'field':
              field = `${path[2]}.${path[3]}`;
              value = path[4];
              switch (field) {
                default:
                  return true;
              }
            case 'filter':
              filter = path.slice(2).join('.');
              switch (filter) {
                default:
                  return true;
              }
            case 'column':
              column = path.slice(2).join('.');
              switch (column) {
                case 'name.update':
                  return false;
                default:
                  return hasRight(`client.field.${column}`, meta);
              }
            case 'entity':
              switch (path[2]) {
                case 'list':
                case 'kanban':
                case 'route':
                  return hasRight('entity.read', meta);
                case 'create':
                  return hasRight('entity.create', meta);
                case 'update':
                  return hasRight('entity.update', meta);
                case 'delete':
                  return hasRight('entity.delete', meta);
                case 'bulk':
                  return hasRight(`entity.bulk.${path.slice(3).join('.')}`, meta);
                case 'download':
                  return hasRight(`entity.download.${path.slice(3).join('.')}`, meta);
                case 'graph':
                  switch (path[3]) {
                    case 'route':
                    case 'list':
                      return true;
                    case 'breakdown':
                    case 'monthlyBreakdown':
                    case 'development':
                    case 'latestNews':
                    case 'rundown':
                    case 'tasks':
                    case 'openTasks':
                      return true;
                    default:
                      return false;
                  }
                default:
                  return hasRight(`entity.${path.slice(2).join('.')}`, meta);
              }
            case 'profile':
              switch (path[2]) {
                case 'read':
                case 'route':
                  return hasRight('collection.read', meta);
                default:
                  return false;
              }
            case 'graph':
              switch (path[2]) {
                case 'route':
                case 'list':
                case 'read':
                  return hasRight('collection.read', meta);
                default:
                  return false;
              }
            case 'task':
              return hasRight(`task.${path.slice(2).join('.')}`, meta);
            case 'user':
              return hasRight(`user.${path.slice(2).join('.')}`, meta);
            case 'notifications':
              return true;
            default:
              return false;
          }
        }
        case 'team': {
          metaTeam = meta?.team;
          metaUser = meta?.user;

          if (utils.isArray(metaTeam)) {
            return !metaTeam.some((team) => !hasRight(attribute, {...meta, team}));
          }

          switch (path[1]) {
            case 'route':
            case 'list':
            case 'read':
              return !utils.isDefined(client?.props) ? true : (client?.props?.hasTeams || client?.props?.hasPrivateTeams);
            case 'switch':
              return !metaTeam ?
                (teams?.filter((t) => t.props.isActive && (!t.props.isPrivate || t.isMember)).length > 1) :
                (metaTeam?.props?.isActive && (!metaTeam?.props?.isPrivate || metaTeam?.isMember))
            case 'update':
              return !ventureIq && (client?.props?.hasTeams || client?.props?.hasPrivateTeams) &&
                atLeastLevel(authGroup, constants.auth.group.clientAdmin);
            case 'create':
              return !ventureIq && (client?.props?.hasTeams || client?.props?.hasPrivateTeams) &&
                atLeastLevel(authGroup, constants.auth.group.clientAdmin) &&
                teams?.filter((t) => t.props.isActive)?.length < client?.props?.teamLimit;
            case 'delete':
              return !ventureIq && (client?.props?.hasTeams || client?.props?.hasPrivateTeams) &&
                atLeastLevel(authGroup, constants.auth.group.clientAdmin) && (
                (metaTeam?.props?.isPrivate || !metaTeam?.props?.isActive || teams.filter((t) => t.props.isActive && !t.props.isPrivate).length > 1) &&
                (+metaTeam?.teamId !== +teamId)
              );
            case 'member':
              switch (path[2]) {
                case 'read':
                case 'list':
                  return hasRight('team.read', meta);
                case 'delete':
                  return !ventureIq && hasRight('team.update', meta) && !metaUser?.isDealLeader &&
                    (proxy || metaUser?.profile?.type !== constants.user.types.proxy) &&
                    !(metaTeam?.props?.isPrivate && +metaUser?.userId === +userId && +metaTeam?.teamId === +teamId);
                case 'create':
                  return !ventureIq && hasRight('team.update', meta);
                default:
                  return false;
              }
            case 'field':
              field = `${path[2]}.${path[3]}`;
              value = path[4];
              switch (field) {
                case 'isPrivate.update':
                  return !metaTeam || (
                    metaTeam?.props?.isPrivate || (
                      (+metaTeam?.teamId !== +teamId || metaTeam.isMember) &&
                      teams.filter((t) => t.props.isActive && !t.props.isPrivate).length > 1
                    )
                  );
                case 'isActive.update':
                  return !metaTeam || (
                    +metaTeam?.teamId !== +teamId &&
                    (metaTeam?.props?.isPrivate || !metaTeam?.props?.isActive ||
                      teams.filter((t) => t.props.isActive && !t.props.isPrivate).length > 1) &&
                    (
                      metaTeam?.props?.isActive ||
                      (teams?.filter((t) => t.props.isActive)?.length < client?.props?.teamLimit)
                    )
                  );
                default:
                  return true;
              }
            case 'column':
              column = path.slice(2).join('.');
              switch (column) {
                default:
                  return hasRight(`team.field.${column}`, meta);
              }
            case 'task':
              return hasRight(`task.${path.slice(2).join('.')}`, meta);
            case 'comment':
              return hasRight(`comment.${path.slice(2).join('.')}`, meta);
            default:
              return false;
          }
        }
        case 'collection': {
          metaCollection = meta?.collection;
          metaEntity = meta?.entity;
          metaSource = meta?.source;
          metaService = meta?.service;

          if (utils.isArray(metaCollection)) {
            return !metaCollection.some((collection) => !hasRight(attribute, {...meta, collection}));
          }

          switch (path[1]) {
            case 'route':
            case 'list':
            case 'read':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'create':
            case 'clone':
            case 'merge':
            case 'update':
            case 'delete':
              if (!metaCollection) {
                return atLeastLevel(authGroup, constants.auth.group.client);
              } else {
                if (utils.isDefined(metaCollection.universeClientId)) {
                  return path[1] === 'update' && atLeastLevel(authGroup, constants.auth.group.clientAdmin);
                } else {
                  return (
                    ( /* all non public collections */
                      metaCollection.visibility !== 'public' &&
                      clientId === metaCollection.ownerClientId &&
                      atLeastLevel(authGroup, constants.auth.group.client) && (
                        (
                          metaCollection.visibility !== 'client' &&
                          +userId === +metaCollection.ownerUserId
                        ) ||
                        (
                          metaCollection.visibility === 'client' && (
                            ventureIq || +teamId === +metaCollection.teamId
                          )
                        )
                      )
                    ) ||
                    ( /* all public collections only viq */
                      metaCollection.visibility === 'public' &&
                      ventureIq && atLeastLevel(authGroup, constants.auth.group.user)
                    )
                  );
                }
              }
            case 'settings':
            case 'themes':
            case 'filters':
            case 'columns':
            case 'tasks':
            case 'notifications':
              return true;
            case 'suggestions':
            case 'rejections':
              return atLeastLevel(authGroup, constants.auth.group.client)
            case 'task':
              return hasRight(`task.${path.slice(2).join('.')}`, meta);
            case 'source':
              switch (path[2]) {
                case 'read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly);
                case 'create':
                  return hasRight('collection.update', meta) && (
                    !metaSource || (
                      ![constants.sources.types.analyser].includes(metaSource.type) && (
                        client?.props?.canScrapeWebsites ||
                        [constants.sources.types.suggestions,
                          constants.sources.types.database].includes(metaSource.type)
                      )
                    )
                  );
                case 'update':
                case 'delete':
                default:
                  return hasRight('collection.update', meta) && (
                    !metaSource ||
                    client?.props?.canScrapeWebsites ||
                    [constants.sources.types.suggestions,
                      constants.sources.types.database].includes(metaSource.type)
                  );
              }
            case 'service':
              switch (path[2]) {
                case 'read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly) && (
                    !metaCollection ||
                    (utils.isDefined(metaCollection.universeClientId) && constants.data.lookup('services', metaService.serviceId).client) ||
                    (!utils.isDefined(metaCollection.universeClientId) && constants.data.lookup('services', metaService.serviceId).collection)
                  );
                case 'create':
                case 'update':
                case 'delete':
                default:
                  return hasRight('collection.update', meta) && (
                    !metaService ||
                    !constants.data.lookup('services', metaService.serviceId).readOnly
                  )
              }
            case 'tagGroup':
              switch (path[2]) {
                case 'read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly);
                case 'create':
                case 'update':
                case 'delete':
                default:
                  return hasRight('collection.update', meta);
              }
            case 'customField':
              switch (path[2]) {
                case 'read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly);
                case 'create':
                case 'update':
                case 'delete':
                default:
                  return hasRight('collection.update', meta);
              }
            case 'wizard':
              return hasRight(`collection.section.${path.slice(2).join('.')}`, meta);
            case 'section':
              section = `${path[2]}.${path[3]}`;
              switch (section) {
                case 'basics.read':
                case 'services.read':
                case 'sources.read':
                case 'categories.read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly)
                case 'basics.create':
                case 'basics.update':
                case 'categories.create':
                case 'categories.update':
                case 'sources.create':
                case 'sources.update':
                case 'services.create':
                case 'services.update':
                  return hasRight('collection.update', meta) && atLeastLevel(authGroup, constants.auth.group.client);
                default:
                  return hasRight(`collection.field.${section}`, meta);
              }
            case 'field':
              field = `${path[2]}.${path[3]}`;
              value = path[4];
              switch (field) {
                case 'visibility.update':
                  switch (value) {
                    case constants.collection.visibility.public:
                      return metaCollection?.visibility === constants.collection.visibility.public || (
                        ventureIq && hasRight('collection.field.visibility.update', meta)
                      );
                    case constants.collection.visibility.user:
                      return metaCollection?.visibility === constants.collection.visibility.user ||
                        hasRight('collection.field.visibility.update', meta);
                    case constants.collection.visibility.client:
                      return metaCollection?.visibility === constants.collection.visibility.client ||
                        hasRight('collection.field.visibility.update', meta);
                    default:
                      return !metaCollection || (
                        hasRight('collection.update', meta) && (
                          (metaCollection?.ownerUserId === userId) || (
                            metaCollection?.visibility === constants.collection.visibility.public &&
                            hasRight('collection.field.visibility.update.public', meta)
                          )
                        )
                      );
                  }
                default:
                  return true;
              }
            case 'filter':
              filter = path.slice(2).join('.');
              switch (filter) {
                default:
                  return true;
              }
            case 'column':
              column = path.slice(2).join('.');
              switch (column) {
                case 'name.update':
                  return false;
                case 'action.read':
                  return hasRight(`collection.update`, meta);
                default:
                  return hasRight(`collection.field.${column}`, meta);
              }
            case 'entity':
              switch (path[2]) {
                case 'list':
                case 'kanban':
                case 'route':
                  return hasRight('collection.read', meta);
                case 'create':
                  return hasRight('collection.update', meta) && hasRight('entity.create', meta);
                case 'delete':
                  return hasRight('collection.update', meta) && hasRight('entity.delete', meta);
                case 'bulk':
                  return hasRight(`entity.bulk.${path.slice(3).join('.')}`, meta);
                case 'download':
                  return hasRight(`entity.download.${path.slice(3).join('.')}`, meta);
                case 'field':
                  field = `${path[3]}.${path[4]}`;
                  switch (field) {
                    case 'collectionTags.update':
                    case 'collectionAnalysisScoreGraph.update':
                      return hasRight('collection.update', meta) && hasRight('entity.update', meta);
                    default:
                      return hasRight(`entity.field.${path.slice(3).join('.')}`, meta);
                  }
                case 'assistant':
                  return hasRight('entity.assistant', meta);
                case 'column':
                  return hasRight(`entity.column.${path.slice(3).join('.')}`, meta) &&
                    hasRight(`collection.entity.field.${path.slice(3).join('.')}`, meta);
                case 'graph':
                  switch (path[3]) {
                    case 'route':
                    case 'list':
                      return hasRight('collection.read', meta);
                    case 'dealflowBreakdown':
                    case 'completeness':
                    case 'recentChanges':
                    case 'tagDistribution':
                    case 'latestNews':
                    case 'dealflowStatus':
                    case 'relevancyDistribution':
                    case 'tasks':
                    case 'openTasks':
                      return true;
                    default:
                      return false;
                  }
                default:
                  return hasRight(`entity.${path.slice(2).join('.')}`, meta);
              }
            case 'profile':
              switch (path[2]) {
                case 'read':
                case 'route':
                  return hasRight('collection.read', meta);
                default:
                  return false;
              }
            case 'graph':
              switch (path[2]) {
                case 'route':
                case 'list':
                  return hasRight('collection.read', meta);
                default:
                  return false;
              }
            default:
              return false;
          }
        }
        case 'database': {
          switch (path[1]) {
            case 'route':
            case 'list':
            case 'read':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly) && (
                !utils.isDefined(client?.props) ? true : client?.props?.hasSearch
              );
            case 'query':
              switch (path[2]) {
                case 'assistant':
                  return atLeastLevel(authGroup, constants.auth.group.client);
                default:
                  return true;
              }
            default:
              return hasRight(`client.${path.slice(1).join('.')}`, meta);
          }
        }
        case 'dealflow': {
          switch (path[1]) {
            case 'route':
            case 'list':
            case 'read':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'graph':
              switch (path[2]) {
                case 'route':
                case 'list':
                  return true;
                default:
                  return false;
              }
            case 'entity':
              switch (path[2]) {
                case 'list':
                case 'kanban':
                case 'route':
                  return hasRight('dealflow.read', meta);
                case 'create':
                  return hasRight('entity.create', meta) && team?.isMember;
                case 'delete':
                  return hasRight('entity.delete', meta) && team?.isMember;
                default:
                  return hasRight(`client.entity.${path.slice(2).join('.')}`, meta);
              }
            default:
              // pass right to client (client.entity) rights
              return hasRight(`client.${path.slice(1).join('.')}`, meta);
          }
        }
        case 'dashboard': {
          switch (path[1]) {
            case 'route':
              return true;
            case 'tasks':
              return hasRight(`task.${path.slice(2).join('.')}`, meta);
            case 'dealflow':
              return hasRight(`dealflow.${path.slice(2).join('.')}`, meta);
            case 'insights':
            case 'recentCollections':
            case 'themeBreakdown':
            case 'periodUpdate':
              return true;
            default:
              return false;
          }
        }
        case 'settings': {
          switch (path[1]) {
            case 'route':
            case 'list':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'profile':
              switch (path[2]) {
                case 'route':
                case 'read':
                case 'create':
                case 'update':
                case 'delete':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly);
                default:
                  return false;
              }
            case 'dealflow':
              switch (path[2]) {
                case 'route':
                case 'read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly);
                case 'create':
                case 'update':
                case 'delete':
                  return (ventureIq && atLeastLevel(authGroup, constants.auth.group.admin)) ||
                    (!ventureIq && atLeastLevel(authGroup, constants.auth.group.clientAdmin));
                default:
                  return false;
              }
            case 'categories':
              switch (path[2]) {
                case 'route':
                case 'read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly);
                case 'create':
                case 'update':
                case 'delete':
                  return (ventureIq && atLeastLevel(authGroup, constants.auth.group.admin)) ||
                    (!ventureIq && atLeastLevel(authGroup, constants.auth.group.clientAdmin));
                default:
                  return false;
              }
            case 'services':
              switch (path[2]) {
                case 'route':
                case 'read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly);
                case 'create':
                case 'update':
                case 'delete':
                  return (ventureIq && atLeastLevel(authGroup, constants.auth.group.admin)) ||
                    (!ventureIq && atLeastLevel(authGroup, constants.auth.group.clientAdmin));
                default:
                  return false;
              }
            case 'connections':
              switch (path[2]) {
                case 'route':
                case 'read':
                case 'create':
                case 'update':
                case 'delete':
                  return (ventureIq && atLeastLevel(authGroup, constants.auth.group.admin)) ||
                    (!ventureIq && client?.props?.hasConnections && atLeastLevel(authGroup, constants.auth.group.clientAdmin));
                default:
                  return false;
              }
            case 'teams':
              switch (path[2]) {
                case 'route':
                case 'read':
                  return !ventureIq && (client?.props?.hasTeams || client?.props?.hasPrivateTeams) &&
                    atLeastLevel(authGroup, constants.auth.group.commentOnly)
                case 'create':
                case 'update':
                case 'delete':
                  return !ventureIq && (client?.props?.hasTeams || client?.props?.hasPrivateTeams) &&
                    atLeastLevel(authGroup, constants.auth.group.clientAdmin);
                default:
                  return false;
              }
            case 'fields':
              switch (path[2]) {
                case 'route':
                case 'read':
                  return !ventureIq && atLeastLevel(authGroup, constants.auth.group.commentOnly);
                case 'create':
                case 'update':
                case 'delete':
                  return !ventureIq && atLeastLevel(authGroup, constants.auth.group.clientAdmin);
                default:
                  return false;
              }
            case 'users':
              switch (path[2]) {
                case 'route':
                case 'read':
                case 'create':
                case 'update':
                case 'delete':
                  return (ventureIq && atLeastLevel(authGroup, constants.auth.group.admin)) ||
                    (!ventureIq && client?.props?.hasUserManagement && atLeastLevel(authGroup, constants.auth.group.clientAdmin));
                default:
                  return false;
              }
            case 'credits':
              switch (path[2]) {
                case 'route':
                case 'read':
                  return !ventureIq && atLeastLevel(authGroup, constants.auth.group.commentOnly);
                case 'create':
                case 'update':
                case 'delete':
                  return !ventureIq && atLeastLevel(authGroup, constants.auth.group.clientAdmin);
                default:
                  return false;
              }
            case 'plan':
              switch (path[2]) {
                case 'route':
                case 'read':
                case 'create':
                case 'update':
                case 'delete':
                  return !ventureIq && atLeastLevel(authGroup, constants.auth.group.clientAdmin);
                default:
                  return false;
              }
            case 'clients':
              switch (path[2]) {
                case 'route':
                case 'read':
                  return ventureIq && atLeastLevel(authGroup, constants.auth.group.user);
                case 'create':
                case 'update':
                case 'delete':
                  return ventureIq && atLeastLevel(authGroup, constants.auth.group.admin);
                default:
                  return false;
              }
            case 'client': {
              switch (path[2]) {
                case 'route':
                case 'read':
                  return hasRight('settings.clients.read', meta) &&
                    hasRight('client.read', meta);
                default:
                  return false;
              }
            }
            default:
              return true;
          }
        }
        case 'search': {
          switch (path[1]) {
            case 'list':
            case 'entity':
            case 'collection':
              return true;
            case 'user':
              return false; // disabled for now
            default:
              return false;
          }
        }
        case 'collaborator': {
          metaCollaborator = meta?.collaborator;

          if (utils.isArray(metaCollaborator)) {
            return !metaCollaborator.some((collaborator) => !hasRight(attribute, {...meta, collaborator}));
          }

          switch (path[1]) {
            case 'route':
            case 'list':
            case 'read':
              return atLeastLevel(authGroup, constants.auth.group.user);
            case 'create':
            case 'update':
              return ventureIq && atLeastLevel(authGroup, constants.auth.group.admin);
            case 'delete':
              return ventureIq && atLeastLevel(authGroup, constants.auth.group.admin) &&
                hasRight('user.delete', {...meta, user: metaCollaborator});
            case 'column':
            case 'field':
              return hasRight(`user.${path.slice(1).join('.')}`, {...meta, user: metaCollaborator});
            default:
              return false;
          }
        }
        case 'view': {
          metaView = meta?.view;

          if (utils.isArray(metaView)) {
            return !metaView.some((view) => !hasRight(attribute, {...meta, view}));
          }

          switch (path[1]) {
            case 'route':
            case 'list':
            case 'read':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'create':
            case 'update':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'delete':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'share':
              return hasRight('team.comment.create', meta);
            default:
              return false;
          }
        }
        case 'task': {
          metaTask = meta?.task;

          if (utils.isArray(metaTask)) {
            return !metaTask.some((task) => !hasRight(attribute, {...meta, task}));
          }

          switch (path[1]) {
            case 'route':
            case 'list':
            case 'read':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'create':
              return atLeastLevel(authGroup, constants.auth.group.client) && team?.isMember;
            case 'update':
            case 'delete':
              return !metaTask || (
                !constants.data.lookup('taskTypes', metaTask?.actionItem?.type)?.system && (
                  +metaTask.userId === +userId ||
                  (atLeastLevel(authGroup, constants.auth.group.client) && team?.isMember) ||
                  atLeastLevel(authGroup, constants.auth.group.clientAdmin)
                )
              );
            case 'markDone':
              return !metaTask || (
                (
                  !metaTask.actionItem?.responsible || metaTask.actionItem?.responsible.length === 0 ||
                  metaTask.actionItem?.responsible.find((r) => +r.userId === +userId)
                ) && atLeastLevel(authGroup, constants.auth.group.client) && team?.isMember
              );
            case 'field':
              field = `${path[2]}.${path[3]}`;
              switch (field) {
                case 'type.update':
                  return false;
                default:
                  return true;
              }
            case 'section':
              section = `${path[2]}.${path[3]}`;
              switch (section) {
                default:
                  return hasRight(`task.field.${section}`, meta);
              }
            default:
              return false;
          }
        }
        case 'file': {
          metaFile = meta?.file;

          if (utils.isArray(metaFile)) {
            return !metaFile.some((file) => !hasRight(attribute, {...meta, file}));
          }

          switch (path[1]) {
            case 'route':
            case 'list':
            case 'read':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'create':
            case 'update':
            case 'delete':
              return atLeastLevel(authGroup, constants.auth.group.client);
            default:
              return false;
          }
        }
        case 'person': {
          metaPerson = meta?.person;

          if (utils.isArray(metaPerson)) {
            return !metaPerson.some((person) => !hasRight(attribute, {...meta, person}));
          }

          switch (path[1]) {
            case 'route':
            case 'list':
            case 'read':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'create':
            case 'update':
            case 'delete':
            case 'enrich':
              return atLeastLevel(authGroup, constants.auth.group.client);
            default:
              return false;
          }
        }
        case 'comment': {
          metaComment = meta?.comment;

          if (utils.isArray(metaComment)) {
            return !metaComment.some((comment) => !hasRight(attribute, {...meta, comment}));
          }

          switch (path[1]) {
            case 'route':
            case 'list':
            case 'read':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'create':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly) && team?.isMember;
            case 'update':
            case 'delete':
              return (!metaComment || (
                    !(metaComment?.user?.type === constants.user.types.agent) &&
                    (+metaComment.userId === +userId || atLeastLevel(authGroup, constants.auth.group.clientAdmin))
                  )
                ) &&
                atLeastLevel(authGroup, constants.auth.group.client) && team?.isMember;
            default:
              return false;
          }
        }
        case 'entity': {
          metaEntity = meta?.entity;

          if (utils.isArray(metaEntity)) {
            return !metaEntity.some((entity) => !hasRight(attribute, {...meta, entity}));
          }

          switch (path[1]) {
            case 'list':
            case 'read':
              return true;
            case 'create':
              return atLeastLevel(authGroup, constants.auth.group.client);
            case 'update':
              return atLeastLevel(authGroup, constants.auth.group.client);
            case 'delete':
              return atLeastLevel(authGroup, constants.auth.group.clientAdmin);
            case 'enrich':
            case 'enrichTeam':
            case 'enrichFinance':
              return atLeastLevel(authGroup, constants.auth.group.client);
            case 'merge':
              switch (path[2]) {
                case 'force':
                  return atLeastLevel(authGroup, constants.auth.group.clientAdmin);
                default:
                  return atLeastLevel(authGroup, constants.auth.group.client);
              }
            case 'bulk':
              action = path.slice(2).join('.');
              switch (action) {
                case constants.entity.bulkActions.applyToExisting:
                case constants.entity.bulkActions.addToCollection:
                case constants.entity.bulkActions.addToOtherCollection:
                case constants.entity.bulkActions.removeFromCollection:
                case constants.entity.bulkActions.rejectFromCollection:
                case constants.entity.bulkActions.createTagGroup:
                case constants.entity.bulkActions.createTagGroups:
                  return hasRight('collection.update', meta);
                case constants.entity.bulkActions.setClientTags:
                  return hasRight('entity.field.clientTags.update', meta);
                case constants.entity.bulkActions.setCollectionTags:
                  return hasRight('entity.field.collectionTags.update', meta);
                case constants.entity.bulkActions.setPersonalRelevancy:
                  return hasRight('entity.field.personalRelevancy.update', meta);
                case constants.entity.bulkActions.setExternalRelevancy:
                  return hasRight('entity.field.externalRelevancy.update', meta);
                case constants.entity.bulkActions.setDealLeader:
                  return hasRight('entity.field.dealLeader.update', meta);
                case constants.entity.bulkActions.setDealflowStatus:
                  return hasRight('entity.field.dealflowStatus.update', meta);
                default:
                  return atLeastLevel(authGroup, constants.auth.group.client);
              }
            case 'download':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'profile':
            case 'browser':
              switch (path[2]) {
                case 'read':
                case 'route':
                  return hasRight('entity.read', meta);
                default:
                  return false;
              }
            case 'field':
              field = `${path[2]}.${path[3]}`;
              switch (field) {
                case 'systemTags.read':
                  return !ventureIq;
                case 'systemTags.update':
                  return false;
                case 'personalRelevancy.read':
                case 'personalRelevancy.update':
                  return !proxy &&
                    !(team?.props?.isExternal && atLeastLevel(authGroup, constants.auth.group.clientAdmin)) &&
                    atLeastLevel(authGroup, constants.auth.group.client) && team?.isMember;
                case 'teamRelevancy.update':
                  return false;
                case 'externalRelevancy.read':
                  return proxyClient;
                case 'externalRelevancy.update':
                  return proxy || (team?.props?.isExternal && atLeastLevel(authGroup, constants.auth.group.clientAdmin));
                case 'dealflowStatus.update':
                  return atLeastLevel(authGroup, constants.auth.group.client) && team?.isMember;
                case 'dealLeader.update':
                  return atLeastLevel(authGroup, constants.auth.group.client) && team?.isMember;
                case 'timeline.update':
                  return hasRight('comment.create', meta);
                case 'patentGraph.read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly) && client?.props?.hasPatents;
                case 'tractionGraph.read':
                case 'tractionFte3M.read':
                case 'tractionFte6M.read':
                case 'tractionFte1Y.read':
                case 'tractionFte3Y.read':
                case 'tractionFunding3M.read':
                case 'tractionFunding6M.read':
                case 'tractionFunding1Y.read':
                case 'tractionFunding3Y.read':
                  return atLeastLevel(authGroup, constants.auth.group.commentOnly) && client?.props?.hasTraction;
                default:
                  return true;
              }
            case 'filter':
              filter = path.slice(2).join('.');
              switch (filter) {
                case 'personalRelevancy.read':
                  return !proxy && atLeastLevel(authGroup, constants.auth.group.client);
                case 'externalRelevancy.read':
                  return proxyClient;
                default:
                  return true;
              }
            case 'column':
              column = path.slice(2).join('.');
              switch (column) {
                case 'name.update':
                  return false;
                default:
                  return hasRight(`entity.field.${column}`, meta);
              }
            case 'section':
              section = `${path[2]}.${path[3]}`;
              switch (section) {
                case 'files.read':
                case 'tasks.read':
                case 'tasks.update':
                  return hasRight(`task.${path[3]}`, meta);
                case 'files.update':
                  return atLeastLevel(authGroup, constants.auth.group.client);
                case 'personalRelevancy.read':
                  return !proxy &&
                    !(team?.props?.isExternal && atLeastLevel(authGroup, constants.auth.group.clientAdmin)) &&
                    atLeastLevel(authGroup, constants.auth.group.client);
                case 'externalRelevancy.read':
                  return proxy || (team?.props?.isExternal && atLeastLevel(authGroup, constants.auth.group.clientAdmin));
                default:
                  return hasRight(`entity.field.${section}`, meta);
              }
            case 'task':
              return hasRight(`task.${path.slice(2).join('.')}`, meta);
            case 'file':
              return hasRight(`file.${path.slice(2).join('.')}`, meta);
            case 'person':
              return hasRight(`person.${path.slice(2).join('.')}`, meta);
            case 'save':
              saver = path.slice(2).join('.');
              switch (saver) {
                case 'collection':
                  return hasRight('collection.create', meta);
                case 'source':
                  return hasRight('collection.source.create', meta);
                case 'view':
                  return hasRight('view.create', meta);
                default:
                  return atLeastLevel(authGroup, constants.auth.group.client);
              }
            case 'assistant':
              assistant = path.slice(2).join('.');
              switch (assistant) {
                case 'persona':
                case 'fields':
                case 'tagGroups':
                case 'sources':
                  return hasRight('collection.update', meta);
                default:
                  return atLeastLevel(authGroup, constants.auth.group.client);
              }
            case 'columns':
              return true;
            case 'filters':
              return true;
            case 'search':
              switch (path[2]) {
                case 'create':
                case 'update':
                case 'delete':
                default:
                  return client?.props?.hasSearch;
              }
            default:
              return false;
          }
        }
        case 'user': {
          metaUser = meta?.user;

          if (utils.isArray(metaUser)) {
            return !metaUser.some((user) => !hasRight(attribute, {...meta, user}));
          }

          switch (path[1]) {
            case 'route':
            case 'list':
              return true;
            case 'read':
              return atLeastLevel(authGroup, constants.auth.group.commentOnly);
            case 'update':
              return !metaUser || (
                ventureIq && (atLeastLevel(authGroup, constants.auth.group.admin) || +metaUser?.userId === +userId)
              ) || (
                !ventureIq && (
                  (client?.props?.hasUserManagement && atLeastLevel(authGroup, constants.auth.group.clientAdmin)) ||
                  +metaUser?.userId === +userId
                )
              );
            case 'create':
              return (ventureIq && atLeastLevel(authGroup, constants.auth.group.admin)) ||
                (!ventureIq && client?.props?.hasUserManagement && atLeastLevel(authGroup, constants.auth.group.clientAdmin));
            case 'delete':
              return !metaUser || (
                ventureIq && atLeastLevel(authGroup, constants.auth.group.admin) && +metaUser?.userId !== +userId && !metaUser?.isDealLeader
              ) || (
                !ventureIq && client?.props?.hasUserManagement && atLeastLevel(authGroup, constants.auth.group.clientAdmin) &&
                +metaUser?.userId !== +userId && !metaUser?.isDealLeader &&
                (proxy || metaUser?.profile?.type !== constants.user.types.proxy)
              );
            case 'column':
              column = path.slice(2).join('.');
              switch (column) {
                default:
                  return hasRight(`user.field.${column}`, meta);
              }
            case 'field':
              field = `${path[2]}.${path[3]}`;
              value = path[4];
              switch (field) {
                case 'authGroup.create':
                case 'authGroup.update':
                  return !utils.isDefined(value) ? +metaUser?.userId !== +userId : (
                    (
                      (ventureIq && metaUser?.type !== constants.user.types.proxy) &&
                      atLeastLevel(value, constants.auth.group.user) && atLeastLevel(authGroup, value)
                    ) || (
                      !(ventureIq && metaUser?.type !== constants.user.types.proxy) &&
                      atLeastLevel(value, constants.auth.group.commentOnly) && atMostLevel(value, constants.auth.group.clientAdmin) &&
                      atLeastLevel(authGroup, value) && (
                        (value === constants.auth.group.commentOnly) ||
                        ( // current user === higher than comment only
                          metaUser &&
                          (
                            metaUser.type !== constants.user.types.regular ||
                            metaUser.authGroup !== constants.auth.group.commentOnly
                          )
                        ) ||
                        (userStats?.regular?.atLeastLevel?.[constants.auth.group.client] < client?.props?.userLimit)
                      )
                    )
                  );
                case 'active.update':
                  return !metaUser || (
                    +metaUser?.userId !== +userId &&
                    (
                      ventureIq ||
                      metaUser.active ||
                      metaUser.authGroup === constants.auth.group.commentOnly ||
                      (userStats?.regular?.atLeastLevel?.[constants.auth.group.client] < client?.props?.userLimit)
                    )
                  );
                case 'apiToken.read':
                case 'apiToken.update':
                  return client?.props?.hasApiConnect && atLeastLevel(authGroup, constants.auth.group.client);
                case 'dataToken.read':
                case 'dataToken.update':
                  return client?.props?.hasLiveData && atLeastLevel(authGroup, constants.auth.group.client);
                case 'portalToken.read':
                case 'portalToken.update':
                  return client?.props?.hasPortal && atLeastLevel(authGroup, constants.auth.group.client);
                default:
                  return true;
              }
            default:
              return false;
          }
        }
        case 'field': {
          metaField = meta?.field;

          if (utils.isArray(metaField)) {
            return !metaField.some((field) => !hasRight(attribute, {...meta, field}));
          }

          switch (path[1]) {
            case 'route':
            case 'list':
            case 'read':
              return true;
            case 'create':
            case 'update':
            case 'delete':
              return !ventureIq && atLeastLevel(authGroup, constants.auth.group.clientAdmin);
            case 'column':
              column = path.slice(2).join('.');
              switch (column) {
                default:
                  return hasRight(`field.field.${column}`, meta);
              }
            case 'field':
              return true;
            default:
              return false;
          }
        }
        case 'tour': {
          switch (path[1]) {
            case constants.tour.types.introduction:
              return !ventureIq && atLeastLevel(authGroup, constants.auth.group.client);
            default:
              return false;
          }
        }
        case 'portal':
          switch (path[1]) {
            case 'route':
              return false;
            default:
              return false;
          }
        case 'dev':
          switch (path[1]) {
            case 'route':
              return false;
            default:
              return system.appBuild() === 'development';
          }
        case 'test':
          switch (path[1]) {
            default:
              return !system.isProduction();
          }
        case 'system':
          switch (path[1]) {
            case 'null':
              return false;
            default:
              return false;
          }
        default:
          return !path;
      }
    }

    return hasRight(attribute, meta);
  }, [authGroup, holderAuthGroup, userId, clientId, teamId, passes, teams, team?.isMember, team?.props, userStats,
    regular, proxy, ventureIq, proxyClient, client?.props, atLeastLevel, atMostLevel]);

  const authorize = useCallback((context) => {
    const {group, attribute, meta, mustLogin = true} = context;
    const userValid = Boolean(normal || meta?.any || (portal && meta?.portal));
    const groupValid = group ? authorizeGroup(group) : true;
    const attributeValid = attribute ? authorizeAttribute(attribute, meta) : true;

    if (!mustLogin) {
      return true;
    } else if (auth?.loggedIn) {
      return userValid && groupValid && attributeValid;
    } else {
      return false;
    }
  }, [auth?.loggedIn, normal, portal, authorizeGroup, authorizeAttribute]);

  const locationRef = useUpdatedRef(location);
  const authorizeAction = useCallback((action) => {
    let authContext;
    if (action?.auth) {
      authContext = utils.isFunction(action?.auth) ? action?.auth() : {...action.auth};
      authContext.meta = utils.isFunction(authContext.meta) ? authContext.meta() : authContext.meta;
    } else if (action?.navigation) {
      const path = linkPath(action?.navigation, locationRef.current);
      const matches = matchRoutes(router.routes, path);
      const match = matches?.find((m) => {
        return utils.comparePath(m.pathname, path.pathname) &&
          utils.isDefined(m.route?.handle?.auth);
      });

      authContext = match?.route?.handle?.auth ? (
        utils.isFunction(match.route.handle.auth) ? match.route.handle.auth() : {...match.route.handle.auth}
      ) : utils.createAuth();

      authContext.meta = utils.isFunction(authContext.meta) ? authContext.meta(match) :
        (authContext.meta ?? {...match?.params});
    } else {
      authContext = utils.createAuth();
    }

    const valid = authorize(authContext);

    if (action && valid) {
      return {
        label: 'name',
        tooltip: null,
        auth: null,
        navigation: null,
        icon: null,
        iconPosition: null,
        children: [],
        ...action
      };
    } else {
      return null;
    }
  }, [authorize, locationRef, router]);

  const warmup = useCallback(() => {
    if (auth?.loggedIn) {
      const warmupFn = portal ? portalClientWarmup.mutation.mutateAsync : clientWarmup.mutation.mutateAsync;
      return utils.observePromise(warmupFn({
        clientId: clientId,
        teamId: teamId,
        $httpParams: { intercept: false }
      }), null, null, null, null, {startDelay: constants.delay.warmup})
        .catch(() => {
          /* SQUASH */
        });
    } else {
      return Promise.resolve();
    }
  }, [auth?.loggedIn, clientId, teamId, portal, clientWarmup.mutation, portalClientWarmup.mutation]);

  const context = useMemo(() => ({
    auth,
    authLocal,
    authSet,
    authLocalSet,
    warmup,
    suspended,
    setSuspended,
    token,
    tokenTime,
    holderToken,
    holderTokenTime,
    whiteLabel,
    userId,
    clientId,
    parentClientId,
    teamId,
    setTeamId,
    ventureIq,
    proxyClient,
    regular,
    proxy,
    normal,
    portal,
    client,
    user,
    team,
    authGroup,
    holderAuthGroup,
    teams,
    passes,
    users,
    userStats,
    authorize,
    authorizeGroup,
    authorizeAttribute,
    authorizeAction
  }), [auth, authLocal, authSet, authLocalSet, token, tokenTime, holderToken, holderTokenTime, whiteLabel,
    userId, clientId, parentClientId, teamId, setTeamId, ventureIq, proxyClient, regular, proxy, normal, portal,
    suspended, user, client, authGroup, holderAuthGroup, team, users, userStats, teams, passes,
    authorize, authorizeGroup, authorizeAttribute, authorizeAction, warmup]);

  const renewRef = useRef({});

  const isPageVisible = usePageVisibility();

  const systemInfoEvent = useEffectEvent(systemInfo.info);
  useEffect(() => {
    if (auth?.loggedIn) {
      const checkRenew = (force) => {
        const lastTokenTime = tokenTime?.getTime() < holderTokenTime?.getTime() ? tokenTime :  holderTokenTime;

        if (lastTokenTime) {
          // token has to be older than the refreshInterval couple of hours
          if (lastTokenTime.getTime() < (Date.now() - system.tokenRefreshInterval())) {
            // user has been active within the refreshTime interval but is inactive for at least 1 min
            if (!systemInfoEvent?.().userIsActive || !isPageVisible || force) {
              if (!renewRef.current.renewing) {
                renewRef.current.renewing = true;
                setSuspended(true);
                Promise.all([
                  doRenew.mutation.mutateAsync({refresh: true}),
                  proxy ? doHolderRenew.mutation.mutateAsync({refresh: true, $httpParams: { intercept: false }}) : null
                ])
                  .catch(() => {
                    /* SQUASH */
                  })
                  .finally(() => {
                    setSuspended(false);
                    renewRef.current.renewing = false;
                  });
              }
            }
          }
        }
      }

      // check on becoming visible
      if (renewRef.current.wasPageVisible === false && isPageVisible) {
        checkRenew(true);
      }
      renewRef.current.wasPageVisible = isPageVisible;

      return utils.observeInterval(() => {
        checkRenew(false);
      }, constants.delay.token);
    }
  }, [doRenew.mutation, doHolderRenew.mutation, auth?.loggedIn, proxy, tokenTime, holderTokenTime, systemInfoEvent, isPageVisible]);

  return <AuthContext.Provider value={context}>
    {props.children}
  </AuthContext.Provider>
};

AuthProvider.propTypes = {
}

export default AuthProvider;
